import { config } from "../../../../../../auth/Office365Config";
import { AuthResponse, UserAgentApplication } from "msal";
import Context from "../../../../../../context/Context";
import * as shareUtil from "../../../../InfoPanel/shareUtil";
import * as dateUtil from "../../../../Events/dateUtil";

import BookingSystem, { IBookingSystem } from "./BookingSystem";
import ModalController from "../../../../../../common/components/Modal/ModalController";
import moment from "moment";
import { nextTmpId } from "../../../../../util/component";
import { getAttributeValue } from "../../../../../../aiim/util/aiimUtil";
import FieldNames from "../../../../../../aiim/datasets/FieldNames";
import type { AuthOptions } from "msal/lib-commonjs/Configuration";
import { Client } from "@microsoft/microsoft-graph-client";
import { DateTimeTimeZone, ScheduleInformation } from "@microsoft/microsoft-graph-types";

export type DateAndTimeZone = Omit<DateTimeTimeZone, "dateTime"> & {
  dateTime: string | Date
}
export interface IScheduleParams {
  availabilityViewInterval?: number,
  schedules: string[],
  startTime: DateAndTimeZone,
  endTime: DateAndTimeZone
}

export default class Office365 extends BookingSystem {
  static instance: IBookingSystem;

  format: string;
  use12Hours: boolean;
  userAgentApplication: UserAgentApplication;

  constructor() {
    super();
    this.type = "office365";
    this.makeUserAgent();
  }

  static appStarting() {
    const key = "indoors-last-href";
    const split = window.location.href.split('#')
    const hash = window.location.hash;
    if (hash &&
       (hash.includes("id_token") || hash.includes("access_token")) &&
       (split.length === 2) &&
       !window.location.href.includes("appid")) {
      const href = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      if (href) {
        window.location.href = href;
      }
    } else {
      window.localStorage.removeItem(key);
    }
  }

  static preLogin() {
    const key = "indoors-last-href";
    const config = Context.instance.config;
    if (config.workspaceReservation && config.workspaceReservation.appId) {
      window.localStorage.setItem(key,window.location.href);
    }
  }

  static getEventStatus = (m365Event, scheduleEmail) => {

    let emailMatch = false, isValidAttendee = false;
    const statusDetails = {
      status: null,
      message: null
    }
    const attendees = (m365Event && m365Event.attendees) || [];
    const i18n = Context.getInstance().i18n;
    if ((typeof scheduleEmail === "string") && scheduleEmail && Array.isArray(attendees)) {
      const lc = scheduleEmail.toLowerCase();
      for (let i=0;i<attendees.length; i++) {
        const attendee = attendees[i];
        if (attendee && attendee.emailAddress && (typeof attendee.emailAddress.address === "string")) {
          isValidAttendee = true;
          if (lc === attendee.emailAddress.address.toLowerCase()) {
            emailMatch = true;
            let status = attendee.status && attendee.status.response;
            status = status.toLowerCase();
            statusDetails.status = status;
            switch (status) {
              case "accepted":
                statusDetails.message = i18n.meetingRooms.attendeeStatus.accepted;
                break;
              case "declined":
                statusDetails.message = i18n.meetingRooms.attendeeStatus.declined;
                break;
              case "tentativelyaccepted":
                statusDetails.message = i18n.meetingRooms.attendeeStatus.tentativelyAccepted;
                break;
              case "none":
                statusDetails.message = i18n.meetingRooms.attendeeStatus.none;
                break;
              case "notresponded":
                statusDetails.message = i18n.meetingRooms.attendeeStatus.none;
                break;
              default:
                statusDetails.message = status;
                break;
            }
          }
        }
      }
    }
    if (isValidAttendee && emailMatch) return statusDetails;
    else if (isValidAttendee && !emailMatch) {
      statusDetails.status = "mismatchEmail";
      statusDetails.message = i18n.meetingRooms.attendeeStatus.emailMismatch;
      return statusDetails;
    }
    else return statusDetails;
  } 

  static wasDeclined = (m365Event) => {
    let declined = false;
    const extension = m365Event && m365Event.extensions && m365Event.extensions[0];
    const scheduleEmail = extension && extension.scheduleEmail;
    const attendees = (m365Event && m365Event.attendees) || [];
    if ((typeof scheduleEmail === "string") && scheduleEmail && Array.isArray(attendees)) {
      const lc = scheduleEmail.toLowerCase();
      attendees.some(attendee => {
        if (attendee && attendee.emailAddress && (typeof attendee.emailAddress.address === "string")) {
          if (lc === attendee.emailAddress.address.toLowerCase()) {
            const status = attendee.status && attendee.status.response;
            if (status === "declined") declined = true;
            return true;
          }
        }
        return false;
      })
    }
    return declined;
  }

  makeUserAgent(updatedAppId?: string, updatedTenantId?: string) {
    const currentURI = window.location.href.split('?')[0];
    // Check configurator
    const configuration = Context.instance.configuration;
    const configurables = configuration.extractConfigurables();
    const appId = updatedAppId || (configurables.workspaceReservation && configurables.workspaceReservation.appId);
    const tenantId = updatedTenantId || (configurables.workspaceReservation && configurables.workspaceReservation.tenantId);
    const isSingleTenant = configurables.workspaceReservation && configurables.workspaceReservation.isSingleTenant;

    const auth: AuthOptions = {
      clientId: appId,
      redirectUri: currentURI,
      postLogoutRedirectUri: currentURI
    };

    const authority = tenantId && `https://login.microsoftonline.com/${tenantId}/`;
    if (authority && isSingleTenant) {
      auth.authority = authority;
    }

    this.userAgentApplication = new UserAgentApplication({
      auth,
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: true
      }
    });
  }

  updateAppId(appId) {
    this.makeUserAgent(appId, null);
  }

  updateTenantId(tenantId) {
    this.makeUserAgent(null, tenantId);
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new Office365()
    }
    return this.instance
  }

  getLoggedInUser = () => {
    return this.userAgentApplication.getAccount()
  }

  login = () => {
    Office365.preLogin();
    return new Promise((resolve, reject) => {
      const loginRequest = {
        scopes: config.scopes
      }

      const authCallback = (error, response) => {
        if (response) {
          resolve(response)
        }
        else {
          reject(error)
        }
      }
      this.userAgentApplication.handleRedirectCallback(authCallback)
      const tryRedirect = () => {
        return this.userAgentApplication.loginRedirect(loginRequest)
      }

      this.userAgentApplication.loginPopup(loginRequest)
        .then(function (loginResponse) {
          resolve("Success")
        }).catch(function (error) {
          console.error(error);
          console.log("error.errorCode",error.errorCode)

          // M365: User isn't notified when pop-up blocker is enabled (Firefox and Safari) #5179
          if (error.errorCode === "popup_window_error" || error.errorCode === "login_progress_error") {
            console.log("There was a loginPopup error",error.errorCode);
            const i18n = Context.instance.i18n;
            ModalController.showMessage(i18n.msal.popupWindowError,i18n.general.warning);

          } else if (error.errorCode !== "user_cancelled") {
            tryRedirect();
          }
        })
    })
  }

  getAccessToken = () => {
    return new Promise<AuthResponse>((resolve, reject) => {
      const accessTokenRequest = {
        scopes: config.scopes
      }
      const authCallback = (error, response) => {
        if (response) {
          if (!response.accessToken) {
            this.userAgentApplication.acquireTokenSilent(accessTokenRequest).then(function(accessTokenResponse) {
              resolve(accessTokenResponse)
            }).catch(e => {
              reject(e)
            })
          } else {
            resolve(response)
          }
        }
        else {
          reject(error)
        }
      }
      this.userAgentApplication.handleRedirectCallback(authCallback)

      const tryPopup = () => {
        return this.userAgentApplication.acquireTokenPopup(accessTokenRequest)
      }

      const tryRedirect = () => {
        return this.userAgentApplication.acquireTokenRedirect(accessTokenRequest)
      }

      this.userAgentApplication.acquireTokenSilent(accessTokenRequest).then(function(accessTokenResponse) {
        resolve(accessTokenResponse)
      }).catch(function (error) {
        tryPopup().then(function(accessTokenResponse) {
          resolve(accessTokenResponse)
        }).catch(function (error) {
          console.error(error);
          if (error.errorCode !== "user_cancelled") {
            tryRedirect();
          }
        })
      })
    })
  }

  getAuthenticatedClient = (accessToken: AuthResponse) => {
    const client = Client.init({
      debugLogging: true,
      authProvider: (done) => {
        done(null, accessToken.accessToken)
      }
    });

    return client;
  }

  getBookingSchedule = async (params: IScheduleParams): Promise<{ value: ScheduleInformation[] }> => {
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken)
    const scheduleInformation: IScheduleParams = {
      availabilityViewInterval: 5,
      schedules: params.schedules,
      startTime: params.startTime,
      endTime: params.endTime
    }

    // add a millisecond to the start time, issue:
    // Unable to make consecutive bookings in a hotel unit with Office365 #4399
    let v = params.startTime && params.startTime.dateTime;
    if (v && typeof v === "string") {
      if (v.substring(v.length - 1, v.length) === "Z") {
        v = moment(v,moment.ISO_8601).add(1,"milliseconds").toISOString();
        params.startTime.dateTime = v;
      }
    } else if (v && typeof (v as Date).getTime === "function") {
      v = moment(v).add(1,"milliseconds").toDate();
      params.startTime.dateTime = v;
    }
    
    return client.api('/me/calendar/getSchedule').post(scheduleInformation);
  }

  checkAvailability = async (scheduleEmails: string[], checkIn: string, checkOut: string, ignoreObjectIds, params)=> {
    let recurrenceDates = null;
    if (params && params.recurrence && params.recurrence.enabled) {
      const mCheckIn = moment(checkIn);
      const mCheckOut = moment(checkOut);
      const recurrence = params.recurrence
      recurrenceDates = this.getRecurringDates(recurrence, mCheckIn, mCheckOut); 
    }
    if(recurrenceDates && recurrenceDates.length > 0) {
      const scheduleIds = [];
      let finalResults = [];
      let availabilityViews = []
      for (let i=0;i<recurrenceDates.length; i++) {
        const dates = recurrenceDates[i];
        const start = dates.start;
        const end = dates.end;
        const results = await this._checkAvailability(scheduleEmails, start, end);
        if (results && results.length > 0) {
          results.forEach((unit) => {
            const scheduleId = unit.scheduleId;
            const availabilityView = unit.availabilityView;
            if(availabilityViews[scheduleId]) {
              availabilityViews[scheduleId] = availabilityViews[scheduleId] + availabilityView;
            } else {
              availabilityViews[scheduleId] = availabilityView;
              scheduleIds.push(scheduleId);
            }
          })
        }
      }
      scheduleIds.forEach((id)=> {
        finalResults.push({
          scheduleId: id,
          availabilityView: availabilityViews[id],
          scheduleItems: []
        })
      })
      return finalResults;
    } else {
      return this._checkAvailability(scheduleEmails, checkIn, checkOut);
    }
  }

  private _checkAvailability = (scheduleEmails: string[], checkIn: string, checkOut: string) => {
    return new Promise<ScheduleInformation[]>((resolve, reject) => {
      this.getAccessToken().then(token => {
        if (token && checkIn && checkOut) {
          const BATCH_SIZE = 15
          const CONCURRENT_REQUESTS = 4

          let chunks = []
          for (var i = 0; i < scheduleEmails.length; i += BATCH_SIZE) {
            chunks.push(scheduleEmails.slice(i, i + BATCH_SIZE))
          }

          const stack = [...chunks];

          const promises: Promise<{ value: ScheduleInformation[] }>[] = [];
          let needPromisesHandler = true;
          const finalResults: ScheduleInformation[] = [];

          //console.log("STACK", stack)
          const processNextBatch = () => {
            if (stack.length !== 0) {
              let nextEmails = stack.pop();
              const params: IScheduleParams = {
                schedules: nextEmails,
                startTime: {
                  dateTime: checkIn,
                  timeZone: "UTC"
                },
                endTime: {
                  dateTime: checkOut,
                  timeZone: "UTC"
                }
              }
              
              const promise = this.getBookingSchedule(params).catch(e => {
                console.error(e);
                throw e;
              }).finally(() => {
                processNextBatch();
              });
              
              promises.push(promise);
            } else if (needPromisesHandler) {
              needPromisesHandler = false
              Promise.all(promises).then(availableUnits => {
                // @ts-ignore
                if (availableUnits.error) {
                  reject()
                } else {
                  availableUnits.forEach(unit => {
                    finalResults.push([].concat.apply([], unit.value))
                  })
                  resolve([].concat.apply([], finalResults))
                }
              }).catch(e => {
                console.error(e)
              })
            }
          }

          for (var j = 0; j < CONCURRENT_REQUESTS; j++) {
            processNextBatch();
          }
        } else if (!token) {
          console.error("No token");
        }
      }).catch(e => {
        console.error(e);
        throw e;
      });
    });
  }

  getTimeZone = async (accessToken?: AuthResponse) => {
    if (accessToken == null) {
      accessToken = await this.getAccessToken();
    }
    const client = this.getAuthenticatedClient(accessToken);
    const response = await client.api('me/mailboxSettings/timeZone').get();
    return response;
  }

  getRecurrence = (params, checkInDate: moment.Moment) => {
    // M365 recurrence properties when making a booking
    let recurrence;
    const daysOfWeek = {};
    daysOfWeek[0] = 'sunday';
    daysOfWeek[1] = 'monday';
    daysOfWeek[2] = 'tuesday';
    daysOfWeek[3] = 'wednesday';
    daysOfWeek[4] = 'thursday';
    daysOfWeek[5] = 'friday';
    daysOfWeek[6] = 'saturday';

    const info = params.recurrence;
    let daysinWords = [];
    if(info.days && (info.days.length > 0)) {
      const days = info.days;
      days.forEach((day)=> {
        daysinWords.push(daysOfWeek[day]);
      })
    }
    const end = info.endDate;
    const endDt = Context.instance.lib.dojo.locale.format(end, {
      selector: "date",
      datePattern: "yyyy-MM-dd"
    })
    const startDt = Context.instance.lib.dojo.locale.format(checkInDate, {
      selector: "date",
      datePattern: "yyyy-MM-dd"
    })
    recurrence = {
      pattern: {
      type: info.type,
      interval: info.interval,
      daysOfWeek: daysinWords
      },
      range: {
        type: 'endDate',
        startDate: startDt,
        endDate: endDt
      }
    }
    return recurrence;
  }

  bookWorkspace = async (params) =>{
    const { email, unitName, checkInDate, checkOutDate, item, bookingType } = params;
    const attributes = item && item.searchResult && item.searchResult.feature && item.searchResult.feature.attributes;
    const areaId = getAttributeValue(attributes, FieldNames.AREA_ID);
    const levelId = getAttributeValue(attributes, FieldNames.LEVEL_ID);
    const unitId = getAttributeValue(attributes, FieldNames.UNIT_ID);
    const scheduleEmail = email;
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    let timezone = await this.getTimeZone(accessToken);
    if(timezone === undefined || timezone === null) timezone = "UTC";
    const epochStartDateTime = new Date(checkInDate).getTime();
    const epochEndDateTime = new Date(checkOutDate).getTime();
    let hoursDiff = (epochStartDateTime - epochEndDateTime)/1000;
    hoursDiff /= (60*60);
    hoursDiff = Math.abs(hoursDiff);
    let reminderHours;
    if(hoursDiff <= 24) reminderHours = 3*60;
    else reminderHours = 18*60;

    const i18n = Context.instance.i18n;
    let subject = i18n.more.bookWorkspace.emailSubject.replace("{workspaceName}", unitName);
    const location = await shareUtil.generateShareUrlAsync(item);
    const locationLink = i18n.more.bookWorkspace.locationLinkPattern;
    const locale: string  = Context.instance.lib.dojo.kernel.locale;
    if (locale === "en" || locale === "en-us") {
      this.use12Hours = true
      this.format = "h:mm A"
    } else {
      this.use12Hours = false
      this.format = "H:mm"
    }
    const startDate = Context.instance.lib.dojo.locale.format(checkInDate, {
      selector: "date",
      datePattern: "yyyy/MM/dd"
    })
    const startTime = Context.instance.lib.dojo.locale.format(checkInDate, {
      selector: "time",
      timePattern: "HH:mm:ss"
    })
    const endDate = Context.instance.lib.dojo.locale.format(checkOutDate, {
      selector: "date",
      datePattern: "yyyy/MM/dd"
    })
    const endTime = Context.instance.lib.dojo.locale.format(checkOutDate, {
      selector: "time",
      timePattern: "HH:mm:ss"
    })

    const startDateTime = startDate + "T" + startTime;
    const endDateTime = endDate + "T" + endTime;
    
    let content = '<div><a href=\"'+ location +'\">'+locationLink+'</a></div>';

    if (params.bookingType === "meetingRooms") {
      if (params.description) {
        let description = params.description.replace(/[\n\r]/g,'<br>');
        content = description+'<br><br><div><a href=\"'+ location +'\">'+locationLink+'</a></div></div>';
      }
    }

    let extensions, showAs, recurrence;
    if (bookingType === "meetingRooms") {
      subject = params && params.title;
      showAs = "busy";
      reminderHours = 15;
      extensions = [
        {
          '@odata.type': 'microsoft.graph.openTypeExtension',
          extensionName: 'Com.Esri.IndoorsMR',
          levelId: levelId,
          unitId: unitId,
          scheduleEmail: scheduleEmail
        }
      ]
    } else {
      showAs = "free";
      extensions = [
        {
          '@odata.type': 'microsoft.graph.openTypeExtension',
          extensionName: 'Com.Esri.Indoors',
          areaId: areaId,
          levelId: levelId,
          unitId: unitId,
          scheduleEmail: scheduleEmail
        }
      ]
    }

    const event = {
      subject: subject,
      body: {
        contentType: "HTML",
        content: content
      },
      start: {
          dateTime: startDateTime,
          timeZone: timezone.value
      },
      end: {
          dateTime: endDateTime,
          timeZone: timezone.value
      },
      location:{
          displayName: unitName
      },
      attendees: [
        {
          emailAddress: {
            address: scheduleEmail,
            name: unitName
          },
          type: "resource"
        }
      ],
      extensions: extensions,
      isOrganizer: true,
      isReminderOn: true,
      reminderMinutesBeforeStart: reminderHours,
      showAs: showAs,
      allowNewTimeProposals: true
    };

    if(params && params.recurrence && params.recurrence.enabled) {
      recurrence = this.getRecurrence(params, checkInDate);
      //@ts-ignore
      event.recurrence = recurrence;
    }
    const response = await client.api('/me/calendar/events').post(event);
    return response;
  }

  updateBooking = async (params) =>{
    const {event, eventId} = params;
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    const response = await client.api("/me/events/"+ eventId).update(event);
    return response;
  }

  cancelBooking = async (params) => {
    const { eventId } = params;
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    const response = await client.api("/me/events/"+ eventId+"/cancel").post(undefined);
    return response;
  }

  getBooking = async(params) => {
    const {eventId} = params;
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    const response = await client.api("/me/events/"+ eventId).get();
    return response;
  }

  getBookings = async(params) => {
    const {currentDateTime, bookingType, bookingDateFilter} = params;
    let dtStart = null;
    let dtEnd = null;
    if (bookingDateFilter && bookingDateFilter.dateStart) {
      dtStart = moment(bookingDateFilter.dateStart).toISOString();
      dtEnd = moment(bookingDateFilter.dateEnd).add(1, "milliseconds").toISOString();
    }

    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    let extensionName = 'Com.Esri.Indoors';
    if (bookingType === "meetingRooms") extensionName = 'Com.Esri.IndoorsMR';

    let api = "/me/calendar/events?";
    api += "$select=start,end,isAllDay,location,extensions,subject,attendees,type";
    api += "&$top=100";
    api += "&$orderBy=start/dateTime";
    api += "&$filter=";
    api += "(isOrganizer eq true)"; 
    if (dtEnd && dtStart) {
      api += " and (end/dateTime gt '" + dtStart + "')";
      api += " and (start/dateTime lt '" + dtEnd + "')";
      api += " and (end/dateTime ge '"+currentDateTime +"')";
    } else api += " and (end/dateTime ge '"+currentDateTime +"')";
    api += " and (Extensions/any(f:f/id eq '"+extensionName+"'))";
    api += "&$expand=Extensions($filter=id eq '"+extensionName+"')";

    const bookings = {value: []};
    const response = await client.api(api).get(); 
    if(response && response.value && response.value.length > 0) {
      const values = response.value;
      for(let i=0;i<values.length;i++) {
        const info = values[i];
        if(info && (info.type !== "seriesMaster") && (info.type !== "occurrence") && (info.type !== "exception")) {
          bookings.value.push(info);
        }
      }
    }
    const finalBookings = await this.getBookingsSeries(bookingDateFilter, currentDateTime, params, bookings); 
    if (finalBookings && finalBookings.value) this._sortBookings(finalBookings.value)
    return finalBookings
  }

  getBookingsSeries =async(bookingDateFilter: { dateStart: Date, dateEnd: Date }, 
    currentDateTime: number, params: { bookingType: string; }, bookings: { value: any[]; })=> {

    let dtStart: string | number = currentDateTime;
    let dtEnd = null;
    if (bookingDateFilter && bookingDateFilter.dateStart) {
      dtStart = moment(bookingDateFilter.dateStart).add(1, "milliseconds").toISOString();
      dtEnd = moment(bookingDateFilter.dateEnd).subtract(1, "milliseconds").toISOString();
    }

    if(!dtEnd) {
      const startDate = (bookingDateFilter && bookingDateFilter.dateStart) || new Date();
      const endDate = startDate.setDate(startDate.getDate() + 7);
      dtEnd = moment(endDate).subtract(1, "milliseconds").toISOString();
    }

    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);   
    let calendarApi = "/me/calendarView?"+ "startDateTime="+dtStart+"&endDateTime="+dtEnd;
    calendarApi += "&$filter="
    calendarApi += "(isOrganizer eq true)";
    calendarApi += " and ((type eq 'occurrence') or (type eq 'exception'))";

    const response = await client.api(calendarApi).get();
    if(response && response.value && response.value.length > 0) {
      const values = response.value;
      for(let i=0;i<values.length;i++) {
        let info = values[i];
        const id = info.seriesMasterId;
        const res = await this.getSeriesMaster(id, params);
        if (res && res.extensions && res.extensions.length > 0) {
          info.extensions = res.extensions;
          info.recurrence = res.recurrence;
          info.masterEvent = res;
          bookings.value.push(info);
        }
      }
    }
    return bookings;
  }

  getSeriesMaster=async(id: string, params: { bookingType: string; })=> {
    const {bookingType} = params;
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);

    let extensionName = 'Com.Esri.Indoors';
    if (bookingType === "meetingRooms") extensionName = 'Com.Esri.IndoorsMR';

    let api = "/me/events/"+id;
    api += "?$select=start,end,isAllDay,location,extensions,subject,attendees,type,recurrence";
    api += "&$filter="
    api += "(isOrganizer eq true)";
    api += " and (Extensions/any(f:f/id eq '"+extensionName+"'))";
    api += "&$expand=Extensions($filter=id eq '"+extensionName+"')";
    const response = await client.api(api).get();  
    return response;
  }

  logout = (reloadUrl) => {
    const url = reloadUrl || window.location.href
    window.open(url)
    this.userAgentApplication.logout()
  }

  _sortBookings = (bookings) => {
    return bookings.sort((resA, resB) => {
      const startTimeA = resA.start.dateTime;
      const startTimeB = resB.start.dateTime;
      return startTimeA > startTimeB ? 1 : startTimeA < startTimeB ? -1 : 0;
    });
  }

  verifyMultipleBookingsM365Recurring = async(task)=> {
    const bookingDateFilter = {
      dateStart: task.checkInDate,
      dateEnd: task.checkOutDate
    }
    const currentDateTime = new Date().getTime();
    const params = {
      bookingType: "hotel"
    }
    const bookings = {value: []};
    const response = await this.getBookingsSeries(bookingDateFilter, currentDateTime, params, bookings);
    if (response && response.value && response.value.length > 0) {
      let value = response.value;
      value = value.filter(e => {
        return !Office365.wasDeclined(e)
      })
      if (value && value.length > 0) {
        return "hasExistingBookings";
      }
    }
  }

  verifyMultipleBookingsM365 = async(task) => {
    const startDateTime = moment(task.checkInDate).toISOString();
    const endDateTime = moment(task.checkOutDate).toISOString();
    const accessToken = await this.getAccessToken();
    const client = this.getAuthenticatedClient(accessToken);
    let extensionName = 'Com.Esri.Indoors';
    let api = "/me/calendar/events?";
    api += "$top=100";
    api += "&$filter="
    api += "(isOrganizer eq true)";
    api += " and (start/dateTime lt '"+endDateTime +"')";
    api += " and (end/dateTime gt '"+startDateTime +"')";
    api += " and (Extensions/any(f:f/id eq '"+extensionName+"'))";
    api += "&$select=extensions,attendees";
    api += "&$expand=Extensions($filter=id eq '"+extensionName+"')";
    const response = await client.api(api).get(); 
    if (response && response.value && response.value.length > 0) {
      let value = response.value;
      value = value.filter(e => {
        return !Office365.wasDeclined(e)
      })
      if (task.eventId) {
        value = value.filter(e => {
          return e.id !== task.eventId
        })
      }
      if (value && value.length > 0) {
        return "hasExistingBookings";
      }
    } else {
      return await this.verifyMultipleBookingsM365Recurring(task)
    }
  }

  queryUnitSchedule = async (roomItem, fromDate: DateAndTimeZone, toDate: DateAndTimeZone) => {
    const user = this.getLoggedInUser();
    const units = Context.getInstance().aiim.datasets && Context.getInstance().aiim.datasets.units;
    const layer = units && units.layer2D;
    const floorField = layer && layer.floorInfo && layer.floorInfo.floorField;
    const feature = roomItem && roomItem.unitFeature;
    const attributes = feature && feature.attributes;
    let scheduleEmail = roomItem && roomItem.scheduleEmail;
    if (!scheduleEmail) scheduleEmail = getAttributeValue(attributes, FieldNames.SCHEDULE_EMAIL);

    if (!user) {
      const status = await this.login();
      if (status !== "Success") {
        return;
      }
    }

    const params = {
      schedules: [scheduleEmail],
      startTime: fromDate,
      endTime: toDate
    };
    const result = await this.getBookingSchedule(params);
    const schedule = result.value && result.value.length === 1 && result.value[0];

    const reservationInfo = {
      reserved: []
    };

    // To run for each schedule item returned from /getSchedule
    const scheduleItemCallback = (item) => {
      // Schedule item dates are returned in UTC, we need to offset them with
      // the system time zone to be accurate in our calendar view

      const o365toDateObj = dateUtil.O365toDate(item.start.dateTime,item.end.dateTime)
      const fromDate = o365toDateObj.fromDate;
      const toDate = o365toDateObj.toDate;

      // Data object that the schedule component can read
      let row = {
        oid: nextTmpId("schedule-item-"),
        fromDate: fromDate.getTime(),
        toDate: toDate.getTime(),
        title: item.subject,
        description: null,
        unitName: getAttributeValue(attributes, FieldNames.NAME),
        levelId: floorField ? attributes[floorField] : null,
        reservee: null,
        feature: null
      };
      reservationInfo.reserved.push(row);
    }

    if (schedule && schedule.scheduleItems && schedule.scheduleItems.length > 0) {
      schedule.scheduleItems.forEach(scheduleItemCallback);
    }

    return reservationInfo;
  }
}